/**
 * Next / previous tag gesture: the rules, with no DOM and no state.
 *
 * The gesture steps through ONE flat list of tags: the tags of the first set, then the tags of the second set, and so on, in the
 * order the tag strip shows them (the active Group's sets in the Group's own order, or every set when no Group is chosen). A set
 * is just where a tag lives: swiping past a set's last tag carries on into the next set's first tag, and past the very last tag
 * of the whole list it wraps round to the first.
 *
 * While stepping, the search shows the ONE tag landed on (a "focus"), not the whole set, so each swipe is a new search. The set's
 * real tags are never changed by stepping (see focus in lib/tag-set-store.ts).
 *
 * The "stay in the set" option keeps the list to the active set's own tags, so past its last tag it wraps to its first.
 */

import type { SetGroup, TagSet } from "@/lib/tag-set-store"

export type StepDirection = "next" | "prev"
/** "continuous": one list of every tag across the sets (default). "set": only the active set's own tags, wrapping inside it. */
export type StepEnd = "continuous" | "set"

export interface TagFocus {
  setId: string
  tag: string
}

export interface StepContext {
  sets: TagSet[]
  groups: SetGroup[]
  activeGroupId: string | null
  activeSetId: string
  focus: TagFocus | null
}

export interface TagItem {
  setId: string
  tag: string
}

export interface StepTarget {
  setId: string
  tag: string
  /** The tag to show alone, or null when the set has only this tag (the set itself is the right search). */
  focusTag: string | null
  /** Went past an end of the list and came round to the other end. */
  wrapped: boolean
  /** 1-based place in the list that was stepped through, and its length. */
  position: number
  total: number
  set: TagSet
}

/** A set's tags without blanks or repeats, in order. */
function tagsOf(set: TagSet): string[] {
  return Array.from(new Set(set.tags.map((tag) => tag.trim()).filter(Boolean)))
}

/** The sets the strip shows, in order. */
export function visibleSetList({ sets, groups, activeGroupId }: Pick<StepContext, "sets" | "groups" | "activeGroupId">): TagSet[] {
  if (!activeGroupId) return sets
  const group = groups.find((g) => g.id === activeGroupId)
  if (!group) return sets
  return group.setIds.map((id) => sets.find((s) => s.id === id)).filter((s): s is TagSet => Boolean(s))
}

/** Every tag of the given sets, set by set. */
export function tagSequence(sets: TagSet[]): TagItem[] {
  return sets.flatMap((set) => tagsOf(set).map((tag) => ({ setId: set.id, tag })))
}

/** The tags the search should use for a set: just the focused tag while stepping, otherwise all of the set's tags. */
export function effectiveSetTags(set: TagSet | undefined, focus: TagFocus | null): string[] {
  if (!set) return []
  if (focus && focus.setId === set.id && set.tags.includes(focus.tag)) return [focus.tag]
  return set.tags
}

/** Where a step goes, or null when there is nowhere to go (fewer than two tags to move between). */
export function nextStepTarget(context: StepContext, direction: StepDirection, end: StepEnd): StepTarget | null {
  const active = context.sets.find((s) => s.id === context.activeSetId)
  const scope = end === "set" ? (active ? [active] : []) : visibleSetList(context)
  const sequence = tagSequence(scope)
  if (sequence.length < 2) return null

  // Which item the search is on now: the focused tag, or the only tag of a one-tag set. A set showing several tags at once
  // (no focus) is not "on" any one of them yet.
  let index = -1
  const focusIsCurrent = context.focus && context.focus.setId === context.activeSetId && active?.tags.includes(context.focus.tag)
  if (focusIsCurrent && context.focus) {
    const focus = context.focus
    index = sequence.findIndex((item) => item.setId === focus.setId && item.tag === focus.tag)
  } else if (active && tagsOf(active).length === 1) {
    index = sequence.findIndex((item) => item.setId === active.id)
  }

  const step = direction === "next" ? 1 : -1
  let targetIndex: number
  let wrapped = false
  if (index === -1) {
    // Enter at the first (or last) tag of the set that is showing, else at the start (or end) of the list.
    const own = sequence.map((item, i) => ({ item, i })).filter(({ item }) => item.setId === context.activeSetId)
    targetIndex = own.length > 0 ? (direction === "next" ? own[0].i : own[own.length - 1].i) : direction === "next" ? 0 : sequence.length - 1
  } else {
    targetIndex = index + step
    if (targetIndex < 0) {
      targetIndex = sequence.length - 1
      wrapped = true
    } else if (targetIndex >= sequence.length) {
      targetIndex = 0
      wrapped = true
    }
  }

  const item = sequence[targetIndex]
  const set = context.sets.find((s) => s.id === item.setId)
  if (!set) return null
  return {
    setId: item.setId,
    tag: item.tag,
    focusTag: tagsOf(set).length > 1 ? item.tag : null,
    wrapped,
    position: targetIndex + 1,
    total: sequence.length,
    set,
  }
}

/** The label after a step: "blue hair", with the set it belongs to when that says more ("Butt") and the place in the list. */
export function tagLabel(tag: string): string {
  return tag.replace(/_/g, " ")
}

export function stepSubtitle(target: Pick<StepTarget, "set" | "position" | "total" | "tag">): string {
  const place = `${target.position} / ${target.total}`
  const name = target.set.name?.trim()
  // A single-tag set is named after its tag and a default name ("Set 3") says nothing, so skip those.
  const informative = name && !/^Set( \d+)?(_\d+)?$/.test(name) && name.toLowerCase() !== tagLabel(target.tag).toLowerCase() && name !== target.tag
  return informative ? `${name} · ${place}` : place
}

// ── The gesture ─────────────────────────────────────────────────────────────────────────────────────────────

export interface SwipeSample {
  startX: number
  startY: number
  endX: number
  endY: number
  durationMs: number
}

/** A swipe must travel at least this far sideways... */
export const MIN_SWIPE_DISTANCE_PX = 64
/** ...and be clearly more sideways than up or down, but forgiving of the curve a thumb draws: the sideways movement only has to
 * be 1.5 times the vertical movement (within about 34 degrees of flat). It looks at where the finger started and ended, not at
 * the path it took, so a quarter-circle thumb swipe still counts as long as it ends far enough to the side. */
export const MIN_HORIZONTAL_RATIO = 1.5
/** ...and be quick: a slow drag is someone reading or panning, not flicking. A finger flick is fast; a mouse drag is a slower,
 * more deliberate movement, so it gets longer. */
export const MAX_SWIPE_DURATION_MS = 700
export const MAX_MOUSE_DRAG_DURATION_MS = 1500

/** Left swipe = next, right swipe = previous (like turning pages), or null when the movement is not a clean sideways flick. */
export function swipeDirection(sample: SwipeSample, maxDurationMs: number = MAX_SWIPE_DURATION_MS): StepDirection | null {
  const dx = sample.endX - sample.startX
  const dy = sample.endY - sample.startY
  if (sample.durationMs > maxDurationMs) return null
  if (Math.abs(dx) < MIN_SWIPE_DISTANCE_PX) return null
  if (Math.abs(dx) < Math.abs(dy) * MIN_HORIZONTAL_RATIO) return null
  return dx < 0 ? "next" : "prev"
}

/** The gesture only counts when it starts in the bottom of the screen, so it never competes with swiping on content higher up. */
export function bottomZoneHeight(viewportHeight: number): number {
  return Math.min(140, Math.max(88, Math.round(viewportHeight * 0.16)))
}

export function startsInBottomZone(y: number, viewportHeight: number): boolean {
  return y >= viewportHeight - bottomZoneHeight(viewportHeight) && y <= viewportHeight
}

/** Sideways trackpad scrolling: add up the horizontal wheel movement and fire once it passes this. */
export const WHEEL_STEP_THRESHOLD = 140
/** After a step, ignore the rest of the same trackpad swipe (it keeps sending events with momentum) for this long. */
export const WHEEL_COOLDOWN_MS = 700

/** Folds one wheel event into the running total. Returns the new total and, when it just passed the threshold, a direction. */
export function accumulateWheel(total: number, deltaX: number, deltaY: number): { total: number; direction: StepDirection | null } {
  // Mostly vertical movement is ordinary scrolling: forget any sideways total.
  if (Math.abs(deltaX) < Math.abs(deltaY) * MIN_HORIZONTAL_RATIO) return { total: 0, direction: null }
  const next = total + deltaX
  if (Math.abs(next) >= WHEEL_STEP_THRESHOLD) return { total: 0, direction: next > 0 ? "next" : "prev" }
  return { total: next, direction: null }
}
